/**
 * Print module — map ko chosen DPI par render karke (OpenLayers "print-to-scale"
 * + "export-map" technique) ek A4-landscape page DOM banata hai aur use ek
 * hidden iframe mein print karta hai (browser print dialog → Save as PDF).
 *
 * Layout (approved design):
 *  ┌──────────────────────────────────────────────┬──────────────┐
 *  │ MAP TITLE                                     │ LEGEND       │
 *  ├──────────────────────────────────────────────┤              │
 *  │   lon labels                                  │ group        │
 *  │ lat┌──────────────────────────────┐ lat  N↑   │  layer + img │
 *  │    │  map image + dashed graticule │           │ ...          │
 *  │    │  scale bar                    │           │              │
 *  │    └──────────────────────────────┘           │              │
 *  ├──────────────────────────────────────────────┤              │
 *  │ logo                  Generated / generated by│              │
 *  └──────────────────────────────────────────────┴──────────────┘
 */
import { toLonLat, fromLonLat, getPointResolution } from "ol/proj";
import OlMap from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";

export const PRINT_DPI_OPTIONS = [96, 150, 200, 300];

/** A4 landscape, sab mm mein. */
export const PRINT_PAGE = { widthMm: 297, heightMm: 210 };

export const PRINT_LAYOUT = {
  pageMargin: 4,
  panelGap: 3,
  legendWidth: 78,
  headerHeight: 15,
  footerHeight: 15,
  // map frame ke chaaron taraf graticule labels ke liye jagah
  mapPad: { top: 11, right: 11, bottom: 12, left: 13 },
};

/** Map ke liye available area (mm) — page layout se derive hota hai. */
export function computeMapAreaMm() {
  const L = PRINT_LAYOUT;
  const leftPanelW =
    PRINT_PAGE.widthMm - 2 * L.pageMargin - L.legendWidth - L.panelGap;
  const leftPanelH = PRINT_PAGE.heightMm - 2 * L.pageMargin;
  return {
    widthMm: leftPanelW - L.mapPad.left - L.mapPad.right,
    heightMm:
      leftPanelH -
      L.headerHeight -
      L.footerHeight -
      L.mapPad.top -
      L.mapPad.bottom,
  };
}

/**
 * Screen par jo dikh raha hai wahi print hota hai, isliye frame ka aspect
 * exported image ke aspect se aata hai; frame available area ke andar
 * "contain" + centred hota hai.
 */
export function computeMapFrameMm(aspect) {
  const area = computeMapAreaMm();
  let w = area.widthMm;
  let h = w / aspect;
  if (h > area.heightMm) {
    h = area.heightMm;
    w = h * aspect;
  }
  return {
    widthMm: w,
    heightMm: h,
    leftMm: (area.widthMm - w) / 2,
    topMm: (area.heightMm - h) / 2,
  };
}

/* ------------------------------------------------------------------ */
/*  Map export                                                         */
/* ------------------------------------------------------------------ */

/**
 * OpenLayers "export-map" example: saare layer canvases ko ek canvas par
 * compose karo (opacity + CSS transform ka dhyan rakhte hue).
 */
function composeMapCanvas(map, size, pixelRatio = 1) {
  const mapCanvas = document.createElement("canvas");
  mapCanvas.width = size[0];
  mapCanvas.height = size[1];
  const ctx = mapCanvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size[0], size[1]);

  const canvases = map
    .getViewport()
    .querySelectorAll(".ol-layer canvas, canvas.ol-layer");

  Array.prototype.forEach.call(canvases, (canvas) => {
    if (!(canvas.width > 0)) return;
    const opacity = canvas.parentNode.style.opacity || canvas.style.opacity;
    ctx.globalAlpha = opacity === "" ? 1 : Number(opacity);

    let matrix;
    const transform = canvas.style.transform;
    if (transform) {
      matrix = transform
        .match(/^matrix\(([^(]*)\)$/)[1]
        .split(",")
        .map(Number);
    } else {
      matrix = [
        parseFloat(canvas.style.width) / canvas.width,
        0,
        0,
        parseFloat(canvas.style.height) / canvas.height,
        0,
        0,
      ];
    }
    // matrix canvas px -> CSS px map karta hai; output ko device px par rakhne
    // ke liye upar se pixelRatio scale lagao
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.transform(...matrix);

    const backgroundColor = canvas.parentNode.style.backgroundColor;
    if (backgroundColor) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(canvas, 0, 0);
  });

  ctx.globalAlpha = 1;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  return mapCanvas;
}

/** Largest "nice" interval with at least `minLines` ACTUAL grid lines inside [min, max]. */
function pickInterval(min, max, minLines = 3) {
  const candidates = [
    10, 5, 2, 1, 0.5, 0.25, 0.2, 0.1, 0.05, 0.025, 0.02, 0.01, 0.005,
    0.0025, 0.002, 0.001, 0.0005,
  ];
  for (const c of candidates) {
    const count = Math.floor(max / c + 1e-9) - Math.ceil(min / c - 1e-9) + 1;
    if (count >= minLines) return c;
  }
  return candidates[candidates.length - 1];
}

function fmtDeg(value, posSuffix, negSuffix) {
  const v = Math.round(Math.abs(value) * 1e6) / 1e6;
  return `${v}°${value < 0 ? negSuffix : posSuffix}`;
}

/**
 * Dashed lat/lon grid canvas par draw karo aur tick positions (frame ke
 * fraction mein) return karo — labels DOM mein frame ke bahar lagte hain.
 */
function drawGraticule(map, canvas, size, dpi, pixelRatio = 1) {
  const view = map.getView();
  const cssSize = [size[0] / pixelRatio, size[1] / pixelRatio];
  const extent = view.calculateExtent(cssSize);
  const [minLon, minLat] = toLonLat([extent[0], extent[1]]);
  const [maxLon, maxLat] = toLonLat([extent[2], extent[3]]);
  const lonStep = pickInterval(minLon, maxLon);
  const latStep = pickInterval(minLat, maxLat);
  const midLon = (minLon + maxLon) / 2;
  const midLat = (minLat + maxLat) / 2;

  const ctx = canvas.getContext("2d");
  const k = dpi / 96;
  ctx.save();
  ctx.strokeStyle = "rgba(60,60,60,0.75)";
  ctx.lineWidth = Math.max(1, 0.9 * k);
  ctx.setLineDash([6 * k, 4 * k]);

  const lonTicks = [];
  for (
    let lon = Math.ceil(minLon / lonStep) * lonStep;
    lon <= maxLon + 1e-9;
    lon += lonStep
  ) {
    const px = map.getPixelFromCoordinate(fromLonLat([lon, midLat]));
    if (!px) continue;
    const x = px[0] * pixelRatio;
    if (x < 0 || x > size[0]) continue;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, size[1]);
    ctx.stroke();
    lonTicks.push({ frac: x / size[0], label: fmtDeg(lon, "E", "W") });
  }

  const latTicks = [];
  for (
    let lat = Math.ceil(minLat / latStep) * latStep;
    lat <= maxLat + 1e-9;
    lat += latStep
  ) {
    const px = map.getPixelFromCoordinate(fromLonLat([midLon, lat]));
    if (!px) continue;
    const y = px[1] * pixelRatio;
    if (y < 0 || y > size[1]) continue;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(size[0], y);
    ctx.stroke();
    latTicks.push({ frac: y / size[1], label: fmtDeg(lat, "N", "S") });
  }
  ctx.restore();
  return { lonTicks, latTicks, lonStep, latStep };
}

/** Scale bar: 1/2/5×10^n metres, frame width ke ~28% tak. */
function computeScaleBar(map, widthPx, pixelRatio = 1, dpi = 96) {
  const view = map.getView();
  // OL ka apna point resolution (ScaleLine bhi yahi use karta hai) — kisi bhi
  // projection ke liye sahi, sirf Web Mercator ke liye nahi
  const groundRes = getPointResolution(
    view.getProjection(),
    view.getResolution(),
    view.getCenter(),
    "m",
  );
  const mpp = groundRes / pixelRatio; // true ground metres per OUTPUT px
  const maxMeters = widthPx * 0.28 * mpp;
  const pow = Math.pow(10, Math.floor(Math.log10(maxMeters)));
  let nice = pow;
  for (const m of [5, 2, 1]) {
    if (m * pow <= maxMeters) {
      nice = m * pow;
      break;
    }
  }
  const km = nice >= 1000;
  const unit = km ? "km" : "m";
  const total = km ? nice / 1000 : nice;
  return {
    widthFrac: nice / mpp / widthPx,
    unit,
    labels: [0, total / 2, total].map((v) => Math.round(v * 100) / 100),
    // paper scale: metres per printed inch = mpp × dpi; inch = 0.0254 m
    denominator: Math.round((mpp * dpi) / 0.0254),
  };
}

/** Visible layer ka clone jo SAME source share karta hai (sources multi-map safe hain). */
function cloneLayerForPrint(layer) {
  if (!layer.getVisible || !layer.getVisible()) return null;
  const source = layer.getSource ? layer.getSource() : null;
  if (!source) return null;
  const common = {
    source,
    visible: true,
    opacity: layer.getOpacity(),
    zIndex: layer.getZIndex(),
    minResolution: layer.getMinResolution(),
    maxResolution: layer.getMaxResolution(),
    extent: layer.getExtent(),
  };
  if (layer instanceof VectorLayer) {
    return new VectorLayer({ ...common, style: layer.getStyle() });
  }
  if (layer instanceof TileLayer) {
    // preload:0 zaroori — Infinity har coarser zoom ke placeholder tiles bhi
    // load karata hai aur rendercomplete un sab ka intezaar karta hai
    return new TileLayer({ ...common, preload: 0 });
  }
  try {
    return new layer.constructor(common);
  } catch (e) {
    return null;
  }
}

/**
 * Screen par JO DIKH RAHA HAI wahi export hota hai — on-screen map ko chhua
 * tak nahi jaata. Ek offscreen clone map banta hai: same size, same center/
 * resolution/rotation, same (shared) sources, lekin pixelRatio = dpi/96, taaki
 * chosen DPI par WMS layers sach mein zyada detail ke saath render hon.
 */
export function exportMapImage(
  map,
  {
    dpi = 200,
    stallMs = 30000,
    maxMs = 240000,
    onProgress = null,
    onLog = null,
  } = {},
) {
  const log = (m) => {
    if (onLog) onLog(m);
  };
  return new Promise((resolve, reject) => {
    const view = map.getView();
    const screenSize = map.getSize();
    if (!screenSize || !screenSize[0] || !screenSize[1]) {
      reject(new Error("Map has no size yet"));
      return;
    }

    // Capture ko page ke map-frame ke ASPECT par lo (screen ke nahi), taaki
    // frame poora bhare. Same center + same scale rakho, aur screen ka poora
    // visible area cover karne ke liye chhoti dimension ko BADHAO (crop kabhi
    // nahi) — jo dikh raha hai wo sab print hota hai, upar-neeche/side mein
    // thoda extra map aa sakta hai.
    const area = computeMapAreaMm();
    const frameAspect = area.widthMm / area.heightMm;
    let cssW = screenSize[0];
    let cssH = screenSize[1];
    if (cssW / cssH > frameAspect) {
      cssH = cssW / frameAspect; // screen zyada chauda -> height badhao
    } else {
      cssW = cssH * frameAspect; // screen zyada uncha -> width badhao
    }
    const cssSize = [Math.round(cssW), Math.round(cssH)];

    const pixelRatio = Math.max(1, dpi / 96);
    const size = [
      Math.round(cssSize[0] * pixelRatio),
      Math.round(cssSize[1] * pixelRatio),
    ];

    const container = document.createElement("div");
    Object.assign(container.style, {
      position: "fixed",
      left: "-100000px",
      top: "0",
      width: `${cssSize[0]}px`,
      height: `${cssSize[1]}px`,
      pointerEvents: "none",
      overflow: "hidden",
    });
    document.body.appendChild(container);

    const clones = map
      .getLayers()
      .getArray()
      .map(cloneLayerForPrint)
      .filter(Boolean);
    log(
      `Capture ${size[0]}×${size[1]} px @ ${dpi} dpi (frame aspect ${(size[0] / size[1]).toFixed(2)}, screen ${screenSize[0]}×${screenSize[1]})`,
    );
    log(`Cloning ${clones.length} visible layer(s) onto offscreen map`);

    const printMap = new OlMap({
      target: container,
      layers: clones,
      controls: [],
      interactions: [],
      pixelRatio,
      view: new View({
        projection: view.getProjection(),
        center: view.getCenter(),
        resolution: view.getResolution(),
        rotation: view.getRotation(),
      }),
    });

    let done = false;
    let stallTimer = null;
    const listeners = [];
    const cleanup = () => {
      clearTimeout(stallTimer);
      clearTimeout(hardTimer);
      listeners.forEach(({ src, type, fn }) => src.un(type, fn));
      try {
        printMap.setTarget(null);
        printMap.dispose();
      } catch (e) {
        /* ignore */
      }
      if (container.parentNode) container.parentNode.removeChild(container);
    };
    const fail = (msg) => {
      if (done) return;
      done = true;
      cleanup();
      reject(new Error(msg));
    };

    // Fixed deadline ki jagah STALL detection: jab tak tiles aa rahe hain,
    // chalne do; sirf tab fail karo jab `stallMs` tak koi tile na aaye.
    const armStall = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(
        () =>
          fail(
            "Map tiles stopped loading (map server slow or unreachable) — try a lower DPI",
          ),
        stallMs,
      );
    };
    const hardTimer = setTimeout(
      () => fail("Map render took too long — try a lower DPI"),
      maxMs,
    );

    let total = 0;
    let loaded = 0;
    clones.forEach((layer) => {
      const src = layer.getSource();
      if (!src || typeof src.getTileGrid !== "function") return; // vector: no tiles
      const onStart = () => {
        total += 1;
        if (onProgress) onProgress({ loaded, total });
      };
      const onDone = () => {
        loaded += 1;
        armStall();
        if (onProgress) onProgress({ loaded, total });
        // milestone logs: har 25%
        const q = Math.floor((loaded * 4) / Math.max(total, 1));
        if (q > (onDone.lastQ || 0) && loaded < total) {
          onDone.lastQ = q;
          log(`Tiles loaded ${loaded}/${total}`);
        }
      };
      [
        ["tileloadstart", onStart],
        ["tileloadend", onDone],
        ["tileloaderror", onDone],
      ].forEach(([type, fn]) => {
        src.on(type, fn);
        listeners.push({ src, type, fn });
      });
    });
    armStall();
    log("Waiting for map tiles…");

    printMap.once("rendercomplete", () => {
      if (done) return;
      done = true;
      try {
        log(`Render complete (${loaded}/${total} tiles) — composing image`);
        const canvas = composeMapCanvas(printMap, size, pixelRatio);
        const graticule = drawGraticule(printMap, canvas, size, dpi, pixelRatio);
        log(
          `Graticule: ${graticule.lonTicks.length} meridians (${graticule.lonStep}°), ${graticule.latTicks.length} parallels (${graticule.latStep}°)`,
        );
        const scaleBar = computeScaleBar(printMap, size[0], pixelRatio, dpi);
        log(
          `Scale 1:${scaleBar.denominator.toLocaleString()} — bar ${scaleBar.labels[2]} ${scaleBar.unit}`,
        );
        // Tainted canvas (bina CORS tile source) par ye throw karega
        const dataUrl = canvas.toDataURL("image/png");
        log(`Image ready (${Math.round(dataUrl.length / 1024)} KB)`);
        cleanup();
        resolve({
          dataUrl,
          width: size[0],
          height: size[1],
          aspect: size[0] / size[1],
          ...graticule,
          scaleBar,
        });
      } catch (e) {
        cleanup();
        reject(
          e && e.name === "SecurityError"
            ? new Error(
                "Map image blocked by browser (a basemap tile source is not CORS-enabled)",
              )
            : e,
        );
      }
    });

    printMap.renderSync();
  });
}

/* ------------------------------------------------------------------ */
/*  Page DOM                                                            */
/* ------------------------------------------------------------------ */

function esc(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const NORTH_ARROW_SVG = `
<svg viewBox="0 0 40 60" width="8mm" height="12mm" xmlns="http://www.w3.org/2000/svg">
  <polygon points="20,2 8,44 20,36" fill="#111"/>
  <polygon points="20,2 32,44 20,36" fill="#fff" stroke="#111" stroke-width="1.5"/>
  <text x="20" y="58" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="13" font-weight="700">N</text>
</svg>`;

/**
 * @param {object} p
 * @param {string} p.title
 * @param {string} [p.subtitle]
 * @param {string} p.createdBy
 * @param {string} p.generatedAt   already formatted
 * @param {string} p.logoUrl
 * @param {object} p.mapImage      exportMapImage() result
 * @param {Array<{name:string, layers:Array<{name:string, legendUrl:string}>}>} p.legendGroups
 * @param {number} [p.dpi]
 * @param {string} [p.basemapName]
 * @param {string} [p.basemapAttribution]
 * @param {string} [p.projection]
 * @param {string} [p.dataSource]
 */
export function buildPrintHtml({
  title,
  subtitle = "",
  createdBy,
  generatedAt,
  logoUrl,
  mapImage,
  legendGroups,
  dpi,
  basemapName = "",
  basemapAttribution = "",
  projection = "WGS 84 / Pseudo-Mercator (EPSG:3857)",
  dataSource = "HCG GIS Portal (GeoServer)",
}) {
  const L = PRINT_LAYOUT;
  const P = L.mapPad;
  const F = computeMapFrameMm(mapImage.aspect || 1.25);
  const fLeft = P.left + F.leftMm;
  const fTop = P.top + F.topMm;

  // graticule labels + small tick marks on the neat line
  const lonLabels = (pos) =>
    mapImage.lonTicks
      .map(
        (t) =>
          `<div class="tick tick-${pos}" style="left:${(t.frac * 100).toFixed(3)}%"><span></span>${esc(t.label)}</div>`,
      )
      .join("");
  const latLabels = (pos) =>
    mapImage.latTicks
      .map(
        (t) =>
          `<div class="tick tick-${pos}" style="top:${(t.frac * 100).toFixed(3)}%"><span></span>${esc(t.label)}</div>`,
      )
      .join("");

  const sb = mapImage.scaleBar;
  const barMm = (sb.widthFrac * F.widthMm).toFixed(2);
  const scaleText = sb.denominator
    ? `Scale 1 : ${sb.denominator.toLocaleString("en")}`
    : "";
  const scaleBarHtml = `
    <div class="scalebar">
      <div class="sb-row">
        <div class="sb-track" style="width:${barMm}mm">
          <div class="sb-labels"><span>${sb.labels[0]}</span><span>${sb.labels[1]}</span><span>${sb.labels[2]}</span></div>
          <div class="sb-bar"><i></i><i class="w"></i><i></i><i class="w"></i></div>
        </div>
        <div class="sb-unit">${esc(sb.unit)}</div>
      </div>
      ${scaleText ? `<div class="sb-scale">${esc(scaleText)}</div>` : ""}
    </div>`;

  const legendHtml = legendGroups.length
    ? legendGroups
        .map(
          (g) => `
      <div class="group">
        <div class="group-title">${esc(g.name)}</div>
        ${g.layers
          .map(
            (l) => `
          <div class="layer">
            <div class="layer-name">${esc(l.name)}</div>
            <img src="${esc(l.legendUrl)}" alt="">
          </div>`,
          )
          .join("")}
      </div>`,
        )
        .join("")
    : `<div class="legend-empty">No visible layers</div>`;

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${esc(title || "Map")}</title>
<style>
  @page { size: A4 landscape; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: "Segoe UI", Arial, Helvetica, sans-serif; color: #1a1a1a; }
  .page { width: ${PRINT_PAGE.widthMm}mm; height: ${PRINT_PAGE.heightMm}mm; padding: ${L.pageMargin}mm; display: flex; gap: ${L.panelGap}mm; page-break-after: always; position: relative; }
  /* double neat line around the sheet */
  .page::before { content: ""; position: absolute; inset: 1.6mm; border: 0.25mm solid #003376; pointer-events: none; }
  .page::after  { content: ""; position: absolute; inset: 2.4mm; border: 0.7mm solid #003376; pointer-events: none; }

  .left { flex: 1; display: flex; flex-direction: column; border: 0.5mm solid #003376; min-width: 0; background: #fff; }
  .header { height: ${L.headerHeight}mm; border-bottom: 0.5mm solid #003376; display: flex; align-items: center; padding: 0 4mm; gap: 4mm; }
  .header .logo { height: ${L.headerHeight - 5}mm; max-width: 55mm; object-fit: contain; flex-shrink: 0; }
  .header .titles { flex: 1; min-width: 0; text-align: center; padding-right: 55mm; }
  .header h1 { margin: 0; font-size: 15pt; font-weight: 700; color: #003376; letter-spacing: 0.3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .header .sub { margin-top: 0.5mm; font-size: 8.5pt; color: #555; letter-spacing: 1.2px; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  .map-area { flex: 1; position: relative; background: #fff; }
  .map-frame { position: absolute; top: ${fTop.toFixed(2)}mm; left: ${fLeft.toFixed(2)}mm; width: ${F.widthMm.toFixed(2)}mm; height: ${F.heightMm.toFixed(2)}mm; border: 0.55mm solid #222; outline: 0.2mm solid #222; outline-offset: 0.8mm; overflow: hidden; }
  .map-frame img.map { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }

  .tick { position: absolute; font-size: 8pt; color: #222; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .tick span { position: absolute; background: #222; }
  .tick-top    { top: -6.2mm; transform: translateX(-50%); }
  .tick-top span    { left: 50%; bottom: -1.9mm; width: 0.25mm; height: 1.6mm; }
  .tick-bottom { bottom: -6.4mm; transform: translateX(-50%); }
  .tick-bottom span { left: 50%; top: -1.9mm; width: 0.25mm; height: 1.6mm; }
  .tick-left   { left: -6.4mm; transform: translate(-50%, -50%) rotate(-90deg); }
  .tick-left span   { left: 50%; bottom: -1.9mm; width: 0.25mm; height: 1.6mm; }
  .tick-right  { right: -6.4mm; transform: translate(50%, -50%) rotate(-90deg); }
  .tick-right span  { left: 50%; top: -1.9mm; width: 0.25mm; height: 1.6mm; }
  .ticks-x, .ticks-y { position: absolute; left: ${fLeft.toFixed(2)}mm; top: ${fTop.toFixed(2)}mm; width: ${F.widthMm.toFixed(2)}mm; height: ${F.heightMm.toFixed(2)}mm; }

  .north { position: absolute; top: 3mm; right: 3mm; }
  .scalebar { position: absolute; left: 3.5mm; bottom: 3.5mm; background: rgba(255,255,255,0.93); border: 0.3mm solid #444; padding: 1.6mm 3mm 1.4mm 2.6mm; box-shadow: 0 0 0 0.8mm rgba(255,255,255,0.6); }
  .sb-row { display: flex; align-items: flex-end; gap: 2mm; }
  .sb-track { position: relative; }
  .sb-labels { display: flex; justify-content: space-between; font-size: 7.5pt; margin-bottom: 0.6mm; font-variant-numeric: tabular-nums; }
  .sb-labels span:first-child { transform: translateX(-50%); }
  .sb-labels span:last-child  { transform: translateX(50%); }
  .sb-bar { display: flex; height: 2.2mm; border: 0.3mm solid #000; }
  .sb-bar i { flex: 1; background: #000; }
  .sb-bar i.w { background: #fff; }
  .sb-unit { font-size: 7.5pt; font-weight: 700; }
  .sb-scale { margin-top: 1mm; font-size: 7.5pt; color: #333; text-align: center; letter-spacing: 0.3px; }

  .footer { height: ${L.footerHeight}mm; border-top: 0.5mm solid #003376; display: flex; align-items: center; justify-content: flex-end; padding: 0 4mm; }
  .meta { text-align: right; font-size: 8.5pt; line-height: 1.5; white-space: nowrap; }
  .meta b { font-weight: 700; color: #003376; }

  .legend { width: ${L.legendWidth}mm; border: 0.5mm solid #003376; display: flex; flex-direction: column; background: #fff; }
  .legend-title { padding: 3mm 4mm 2.4mm; border-bottom: 0.5mm solid #003376; font-weight: 700; font-size: 12pt; letter-spacing: 2px; color: #003376; display: flex; align-items: center; justify-content: space-between; }
  .legend-body { flex: 1; padding: 3mm; column-count: 2; column-gap: 3mm; column-fill: auto; overflow: hidden; }
  .group { break-inside: avoid; margin-bottom: 3mm; }
  .group-title { background: #eef3fa; border-left: 1mm solid #003376; padding: 1mm 2mm; font-weight: 700; font-size: 9pt; color: #003376; margin-bottom: 1.5mm; }
  .layer { margin: 0 0 2mm 1mm; break-inside: avoid; }
  .layer-name { font-weight: 600; font-size: 8pt; margin-bottom: 0.7mm; color: #222; }
  .layer img { display: block; max-width: 100%; }
  .legend-empty { font-size: 9pt; color: #777; }

</style></head>
<body>
  <div class="page">
    <div class="left">
      <div class="header">
        <img class="logo" src="${esc(logoUrl)}" alt="logo">
        <div class="titles">
          <h1>${esc(title || "Map")}</h1>
          ${subtitle ? `<div class="sub">${esc(subtitle)}</div>` : ""}
        </div>
      </div>
      <div class="map-area">
        <div class="ticks-x">${lonLabels("top")}${lonLabels("bottom")}</div>
        <div class="ticks-y">${latLabels("left")}${latLabels("right")}</div>
        <div class="map-frame">
          <img class="map" src="${mapImage.dataUrl}" alt="map">
          <div class="north">${NORTH_ARROW_SVG}</div>
          ${scaleBarHtml}
        </div>
      </div>
      <div class="footer">
        <div class="meta">
          <div><b>Generated:</b> ${esc(generatedAt)}</div>
          <div><b>Generated by :</b> ${esc(createdBy || "—")}</div>
        </div>
      </div>
    </div>
    <div class="legend">
      <div class="legend-title">LEGEND</div>
      <div class="legend-body">${legendHtml}</div>
    </div>
  </div>
</body></html>`;
}

/* ------------------------------------------------------------------ */
/*  Iframe printing                                                     */
/* ------------------------------------------------------------------ */

/**
 * Hidden iframe mein document banao, saari images load hone do, phir print.
 * Promise print dialog band hone (afterprint) ya timeout par resolve hoti hai.
 */
export function printInIframe(
  html,
  { imageTimeoutMs = 20000, onLog = null } = {},
) {
  const log = (m) => {
    if (onLog) onLog(m);
  };
  return new Promise((resolve, reject) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    Object.assign(iframe.style, {
      position: "fixed",
      right: "0",
      bottom: "0",
      width: "0",
      height: "0",
      border: "0",
      opacity: "0",
      pointerEvents: "none",
    });
    document.body.appendChild(iframe);

    const cleanup = () => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    let doc;
    try {
      doc = iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
    } catch (e) {
      cleanup();
      reject(e);
      return;
    }

    const imgs = Array.from(doc.images);
    log(`Page built — waiting for ${imgs.length} image(s) (map, logo, legend)`);
    const waits = imgs.map(
      (img) =>
        new Promise((res) => {
          if (img.complete) return res();
          img.onload = () => res();
          img.onerror = () => res(); // broken legend image print ko na roke
        }),
    );
    const timeout = new Promise((res) => setTimeout(res, imageTimeoutMs));

    Promise.race([Promise.all(waits), timeout]).then(() => {
      log("Opening print dialog (choose “Save as PDF” to export)");
      const win = iframe.contentWindow;
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        // thoda ruk ke hatao — kuch browsers afterprint se pehle hi resolve karte hain
        setTimeout(cleanup, 1500);
        resolve();
      };
      win.addEventListener("afterprint", finish);
      try {
        win.focus();
        win.print();
        // afterprint na aaye (kuch browsers) to bhi cleanup ho jaye
        setTimeout(finish, 60000);
      } catch (e) {
        cleanup();
        reject(e);
      }
    });
  });
}
